import { useState, useMemo } from 'react';
import { branches, occupations } from './data/militaryOccupations';
import './OccupationSelector.css';

// Detect paygrade category from rank string like "E-7 · Master Sergeant"
function detectCategory(rank) {
  if (!rank) return null;
  const prefix = rank.trim().toUpperCase();
  if (prefix.startsWith('W-') || prefix.startsWith('CW')) return 'warrant';
  if (prefix.startsWith('O-')) return 'officer';
  if (prefix.startsWith('E-')) return 'enlisted';
  return null;
}

const CATEGORY_LABELS = {
  enlisted: 'Enlisted',
  warrant:  'Warrant Officer',
  officer:  'Commissioned Officer',
};

export default function OccupationSelector({ value, onChange, profileRank }) {
  const [selectedBranch, setSelectedBranch] = useState(value?.branch || '');
  const [selectedCode,   setSelectedCode]   = useState(value?.code   || '');
  const [branchOpen,     setBranchOpen]      = useState(false);
  const [codeOpen,       setCodeOpen]        = useState(false);
  const [search,         setSearch]          = useState('');

  const currentBranch = branches.find(b => b.id === selectedBranch);
  const systemLabel   = currentBranch?.system || 'Code';

  // Detect category from profile rank
  const detectedCategory = detectCategory(profileRank);

  // Filter codes by branch and category
  const filteredCodes = useMemo(() => {
    if (!selectedBranch) return [];
    const all = occupations[selectedBranch] || [];
    let list = detectedCategory ? all.filter(o => o.category === detectedCategory) : all;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(o =>
        o.code.toLowerCase().includes(q) ||
        o.title.toLowerCase().includes(q)
      );
    }
    return list;
  }, [selectedBranch, detectedCategory, search]);

  const currentOcc = filteredCodes.find(o => o.code === selectedCode)
    || (occupations[selectedBranch] || []).find(o => o.code === selectedCode);

  const handleBranchSelect = (branchId) => {
    setSelectedBranch(branchId);
    setSelectedCode('');
    setSearch('');
    setBranchOpen(false);
    if (onChange) onChange({ branch: branchId, code: '', title: '' });
  };

  const handleCodeSelect = (code, title) => {
    setSelectedCode(code);
    setCodeOpen(false);
    setSearch('');
    if (onChange) onChange({ branch: selectedBranch, code, title });
  };

  return (
    <div className="occ-selector">

      {/* Step 1 — Branch */}
      <div className="occ-field">
        <label className="occ-label">Branch of Service</label>
        <div className="occ-dropdown-wrap">
          <button
            type="button"
            className={`occ-trigger${branchOpen ? ' open' : ''}${selectedBranch ? ' has-value' : ''}`}
            onClick={() => { setBranchOpen(v => !v); setCodeOpen(false); }}
          >
            <span>{currentBranch ? currentBranch.label : 'Select your branch'}</span>
            <svg className="occ-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          {branchOpen && (
            <div className="occ-menu">
              {branches.map(b => (
                <button key={b.id} type="button"
                  className={`occ-option${selectedBranch === b.id ? ' selected' : ''}`}
                  onClick={() => handleBranchSelect(b.id)}>
                  <span className="occ-option-label">{b.label}</span>
                  <span className="occ-option-system">{b.system}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Step 2 — Code (only after branch selected) */}
      {selectedBranch && (
        <div className="occ-field">
          <div className="occ-label-row">
            <label className="occ-label">{systemLabel} — Occupation Code</label>
            {detectedCategory && (
              <span className="occ-category-badge">
                {CATEGORY_LABELS[detectedCategory]} codes
              </span>
            )}
          </div>
          <div className="occ-dropdown-wrap">
            <button
              type="button"
              className={`occ-trigger${codeOpen ? ' open' : ''}${selectedCode ? ' has-value' : ''}`}
              onClick={() => { setCodeOpen(v => !v); setBranchOpen(false); setSearch(''); }}
            >
              <span>
                {currentOcc
                  ? `${currentOcc.code} — ${currentOcc.title}`
                  : `Select your ${systemLabel}`}
              </span>
              <svg className="occ-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>

            {codeOpen && (
              <div className="occ-menu occ-menu-codes">
                <div className="occ-search-wrap">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input
                    type="text"
                    autoFocus
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search by code or title..."
                  />
                </div>
                {filteredCodes.length === 0 && (
                  <div className="occ-no-results">No results for "{search}"</div>
                )}
                {filteredCodes.map(({ code, title, category }) => (
                  <button key={code} type="button"
                    className={`occ-option${selectedCode === code ? ' selected' : ''}`}
                    onClick={() => handleCodeSelect(code, title)}>
                    <span className="occ-option-code">{code}</span>
                    <span className="occ-option-title">{title}</span>
                    <span className={`occ-option-cat occ-cat-${category}`}>{category}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Result chip */}
      {selectedCode && currentOcc && (
        <div className="occ-result">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          <div>
            <div className="occ-result-code">{currentOcc.code} · {currentBranch?.label}</div>
            <div className="occ-result-title">{currentOcc.title}</div>
          </div>
        </div>
      )}

    </div>
  );
}